import React, { useState, useEffect, useRef } from 'react';
import { ArrowPathIcon, PaperAirplaneIcon, FaceSmileIcon } from '@heroicons/react/24/outline';
import { sendMessageApi } from '../../../../services/chatService';
import { chatbotService } from '../../../../services/chatbotService';
import { useAuth } from '../../../../context/AuthContext';
import { getUser } from '../../../../lib/storage';
import { renderChatMarkdown } from '../../../../utility/renderMarkdownText';
import { ActionConfigModal, ActionConfig } from '../../../common/ActionConfigModal';

const defaultSystem = `**Role - Primary Function:**
You are an AI chatbot who helps users with their inquiries, issues and requests. You aim to provide excellent support with clear, concise responses.

**Key Guidelines:**
- Be friendly, professional, and helpful.
- Ask clarifying questions when needed.
- Avoid making assumptions. Do not provide sensitive data.`;

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
};

interface PlaygroundTabProps {
  chatbot: any;
}

const PlaygroundTab: React.FC<PlaygroundTabProps> = ({ chatbot }) => {
  const auth = useAuth();
  const user = auth?.user;
  const token = auth?.user?.token;

  const messagesEndRef: any = useRef(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Settings
  const [model, setModel] = useState('GPT-4o');
  const [temperature, setTemperature] = useState(0);
  const [systemPreset, setSystemPreset] = useState('Base Instructions');
  const [systemInstructions, setSystemInstructions] = useState(defaultSystem);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const [shouldFocusInput, setShouldFocusInput] = useState(false);

  // Messages
  const initialMessages: ChatMessage[] = [
    { id: 'm-1', role: 'assistant', content: 'Hi! What can I help you with?' },
  ];
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Action popup state
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [actionInitial, setActionInitial] = useState<Partial<ActionConfig> | undefined>(undefined);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (shouldFocusInput) {
      textareaRef.current?.focus();
      setShouldFocusInput(false);
    }
  }, [shouldFocusInput]);

  const resetChat = () => {
    setMessages(initialMessages);
    setConversationId(undefined);
    setIsLoading(false);
  };

  const handleInput = (e: any) => {
    setInput(e.target.value);
    const textarea: any = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  };

  const resetSystem = () => {
    setSystemPreset('Base Instructions');
    setSystemInstructions(defaultSystem);
  };

  const handleSubmitActionConfig = (cfg: ActionConfig) => {
    // For now just log the configuration. You can persist via API later.
    console.log('Submit Action Config:', cfg);
    setIsActionModalOpen(false);
  };

  const sendMessage = async () => {
    const trimmed = input.trim();
    if (!trimmed) {
      setShouldFocusInput(true);
      return;
    }
    if (isLoading) {
      setShouldFocusInput(true);
      return;
    }

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: trimmed,
    };
    // Append user message
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    // add loading placeholder message
    const loadingId = `loading-${Date.now()}`;
    const loadingMsg: ChatMessage = {
      id: loadingId,
      role: 'assistant',
      content: '__loading__', // sentinel content to render loader
    };
    setMessages((prev) => [...prev, loadingMsg]);
    setIsLoading(true);

    try {
      if (!chatbot || !user) {
        // remove loading message and set error
        setMessages((prev) =>
          prev.map((m) => (m.id === loadingId ? { ...m, content: 'Error: chatbot or user not available.' } : m))
        );
        setShouldFocusInput(true);
        setIsLoading(false);
        return;
      }

      // Get tenant and user IDs robustly
      const lsTenantId = localStorage.getItem('agentic_tenant_id') || localStorage.getItem('tenant_id');
      const user1 = getUser();
      const envTenantId = user1?.tenantId;

      let storedUser: any = undefined;
      try {
        storedUser = JSON.parse(localStorage.getItem('agentic_user_data') || 'null') || undefined;
      } catch {
        storedUser = undefined;
      }

      const ctxTenantId = (user as any)?.tenantId || (user as any)?.user?.tenantId;
      const storedTenantId = storedUser?.tenantId || storedUser?.user?.tenantId;
      const tenantId = ctxTenantId || lsTenantId || storedTenantId || envTenantId;

      const ctxUserId = (user as any)?.id || (user as any)?.user?.id;
      const storedUserId = storedUser?.id || storedUser?.user?.id;
      const finalUserId = ctxUserId || storedUserId;

      console.log('Send Message Debug:', {
        userTenantId: (user as any)?.tenantId || (user as any)?.user?.tenantId,
        lsTenantId,
        envTenantId,
        finalTenantId: tenantId,
        chatbotId: chatbot.id,
        userId: finalUserId,
        message: trimmed,
      });

      const response = await sendMessageApi(
        {
          chatbotId: chatbot.id,
          tenantId: tenantId,
          userId: finalUserId,
          message: trimmed,
          conversationId,
        }
      );

      if (!conversationId && response?.conversationId) {
        setConversationId(response.conversationId);
      }

      const assistantReply = response?.reply || 'Sorry, no response.';
      // replace loading message with assistant reply
      setMessages((prev) =>
        prev.map((m) => (m.id === loadingId ? { id: `a-${Date.now() + 1}`, role: 'assistant', content: assistantReply } : m))
      );
      setShouldFocusInput(true);
    } catch (err) {
      // replace loading message with error message
      setMessages((prev) =>
        prev.map((m) =>
          m.id === loadingId
            ? { id: `e-${Date.now() + 2}`, role: 'assistant', content: 'Error sending message. Please try again.' }
            : m
        )
      );
      setShouldFocusInput(true);
    } finally {
      setShouldFocusInput(true);
      setIsLoading(false);
    }
  };

  // Simple three-dot loader component (CSS animation inside component)
  const ThreeDotLoader: React.FC = () => (
    <span className="inline-flex items-center space-x-1">
      <style>
        {`
        @keyframes dotBounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.6; }
          40% { transform: translateY(-6px); opacity: 1; }
        }
        .dot {
          width: 6px;
          height: 6px;
          border-radius: 9999px;
          background: currentColor;
          display: inline-block;
        }
        .dot1 { animation: dotBounce 1s infinite; animation-delay: 0s; }
        .dot2 { animation: dotBounce 1s infinite; animation-delay: 0.15s; }
        .dot3 { animation: dotBounce 1s infinite; animation-delay: 0.3s; }
      `}
      </style>
      <span className="dot dot1" />
      <span className="dot dot2" />
      <span className="dot dot3" />
    </span>
  );

  return (
    <div className="p-6">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left Controls */}
        <div className="xl:col-span-1 space-y-5">
          {/* Playground Header */}
          <div className="bg-white border rounded-2xl p-5">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-agentic-text-primary">Playground</h3>
            </div>
            <div className="mt-3 flex items-center gap-3 rounded-lg border p-3">
              <span className="inline-flex items-center">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 mr-2" />
                <span className="text-sm font-medium text-emerald-700">Trained</span>
              </span>
              <span className="text-xs text-agentic-text-secondary">Last trained just now</span>
            </div>
          </div>

          {/* Model Settings */}
          <div className="bg-white border rounded-2xl p-5 space-y-5">
            <div>
              <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                Model
              </label>
              <select
                value={model}
                onChange={(e) => setModel(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
              >
                <option>GPT-4o</option>
                <option>GPT-4.1</option>
                <option>Claude 3.5</option>
                <option>Llama 3.1 70B</option>
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-agentic-text-primary">
                  Temperature
                </label>
                <span className="text-sm text-agentic-text-secondary">{temperature}</span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={temperature}
                onChange={(e) => setTemperature(Number(e.target.value))}
                className="w-full accent-agentic-primary-blue"
              />
              <div className="flex items-center justify-between text-xs text-agentic-text-secondary mt-1">
                <span>Reserved</span>
                <span>Creative</span>
              </div>
            </div>
          </div>

          {/* AI Actions */}
          <div className="bg-white border rounded-2xl p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-agentic-text-primary">AI Actions</div>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-lg text-white bg-agentic-primary-blue hover:bg-agentic-primary-blue/90"
                onClick={() => setIsActionModalOpen(true)}
              >
                + Add Action
              </button>
            </div>
            <div className="rounded-lg border border-dashed p-4 text-sm text-agentic-text-secondary text-center">
              No actions found
            </div>
          </div>

          {/* System Instructions */}
          <div className="bg-white border rounded-2xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-agentic-text-primary">
                Instructions (System prompt)
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={systemPreset}
                  onChange={(e) => setSystemPreset(e.target.value)}
                  className="px-2 py-1.5 rounded-lg border border-gray-200 text-sm"
                >
                  <option>Base Instructions</option>
                  <option>Support Assistant</option>
                  <option>Sales Assistant</option>
                </select>
                <button
                  type="button"
                  className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"
                  onClick={resetSystem}
                  aria-label="Reset system prompt"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
            <textarea
              rows={10}
              value={systemInstructions}
              onChange={(e) => setSystemInstructions(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent"
            />
          </div>
        </div>

        {/* Right Chat Preview */}
        <div className="xl:col-span-2">
          <div className="w-full max-w-[840px] mx-auto my-6 bg-white/80 backdrop-blur rounded-2xl border shadow-sm overflow-hidden flex flex-col min-h-[680px]">
            {/* Chat Header */}
            <div className="h-12 bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light text-white flex items-center justify-between px-4">
              <div className="font-medium">Live Chat</div>
              <button
                onClick={resetChat}
                className="p-1 rounded hover:bg-white/20"
                aria-label="Reset chat"
              >
                <ArrowPathIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Messages */}
            <div
              className="overflow-y-auto p-4 space-y-3 bg-[#f7f9fc]"
              style={{ height: '700px' }}
            >
              {messages.map((m) => {
                const isUser = m.role === 'user';
                const isLoadingMsg = m.content === '__loading__';
                return (
                  <div key={m.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm shadow ${isUser
                          ? 'bg-gradient-to-r from-agentic-primary-blue to-agentic-primary-blue-light text-white'
                          : 'bg-agentic-gray-50 text-agentic-text-primary border'
                        }`}
                    >
                      <div className="message-content">
                        {isLoadingMsg ? (
                          // render three-dot loader for loading messages
                          <div className="flex items-center gap-2">
                            {/* <span className="text-agentic-text-secondary text-sm">Thinking</span> */}
                            <ThreeDotLoader />
                          </div>
                        ) : (
                          renderChatMarkdown(m.content)
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Powered by */}
            <div className="px-4 pb-1 text-center text-[11px] text-agentic-text-secondary/80">
              {/* Powered by text if needed */}
            </div>

            {/* Input */}
            <div className="p-3 border-t">
              <div className="flex items-end gap-2 bg-white border rounded-xl px-3 py-3">
                <button
                  className="p-1 text-agentic-text-secondary hover:text-agentic-text-primary"
                  type="button"
                  disabled={isLoading}
                >
                  <FaceSmileIcon className="h-5 w-5" />
                </button>
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={handleInput}
                  rows={1}
                  placeholder="Message..."
                  className="flex-1 resize-none outline-none text-base bg-transparent overflow-auto"
                  style={{ maxHeight: '120px' }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  disabled={isLoading}
                />
                <button
                  onClick={sendMessage}
                  className={`p-2 rounded-md text-white ${isLoading ? 'bg-gray-300 cursor-not-allowed' : 'bg-agentic-primary-blue hover:bg-agentic-primary-blue/90'}`}
                  disabled={isLoading}
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Action Config Modal */}
      {/* <ActionConfigModal
        isOpen={isActionModalOpen}
        onClose={() => setIsActionModalOpen(false)}
        initialConfig={actionInitial}
        onSubmit={handleSubmitActionConfig}
      /> */}
    </div>
  );
};

export default PlaygroundTab;
